import {
  ExamCenterProctorType,
  FilterOperator,
  OrderType,
  useGetAllExamCenterCoursesQuery,
  useGetAllProvincesQuery,
  useGetExamCenterLocationsByProvinceIdQuery,
  useSearchExamCenterProctorsQuery,
  useSearchExamCenterRoomsQuery,
  useValidatedExamEventsLazyQuery,
} from '@exam-center/graphql';
import { useNavigate } from '@exam-center/router';
import { Loading } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { dayjs, randomUUID } from '@shared/utils';
import { useCallback, useEffect, useMemo } from 'react';

import { RecurringExamEventCreationForm } from '../../components';
import { EXAM_EVENT_CREATION_ERROR_MESSAGE_MAP } from '../../constants';
import {
  EventEndType,
  type RecurringExamEventCreationFormType,
  useRecurringExamEventCreation,
  useRecurringExamEventCreationForm,
} from '../../providers';
import { getExamEventCreationErrorCode } from '../../utils';

import { mapRecurringExamEventCreationFormToValidateExamEventsCreationInput } from './mapper';

export interface RoomData {
  id: string;
  name: string;
  seat: number;
  floor: string;
  roomNumber: string | null;
  building: string;
}

interface RecurringExamEventCreationLogicProps {
  examCenterCode: string;
  year: {
    param: string;
    value: number;
  };
  month: {
    param: string;
    value: number;
  };
}

export default function RecurringExamEventCreationLogic(
  props: Readonly<RecurringExamEventCreationLogicProps>,
) {
  const {
    examCenterCode,
    year: { param: yearParam, value: year },
    month: { param: monthParam, value: month },
  } = props;
  const { examEvent, setBuilding } = useRecurringExamEventCreation();
  const { setExamEvent, setValidateExamEventsData } = useRecurringExamEventCreation();

  const { showSnackbar } = useSnackbar();

  const navigate = useNavigate();
  const [validatedExamEvents, { loading: isValidatingExamEvents }] =
    useValidatedExamEventsLazyQuery();
  const formContext = useRecurringExamEventCreationForm();
  const { watch } = formContext;

  const { location } = watch();
  const examRecurringStartDate = watch('examDateGroup.examRecurringStartDate');
  const examRecurringEndDate = watch('examDateGroup.examRecurringEndDate');
  const isProvinceSelected = !!location?.province?.value;

  const { data: getAllProvincesResult, loading: isLoadingProvincesOptions } =
    useGetAllProvincesQuery();

  function isSelected(value: Date | Record<string, string> | null | undefined) {
    return value !== undefined && value !== null;
  }

  const { data: examCenterCoursesData, loading: isLoading } = useGetAllExamCenterCoursesQuery({
    variables: {
      getAllExamCenterCoursesInput: {
        examCenterCode,
        sessionCount: 1,
      },
    },
    fetchPolicy: 'network-only',
  });

  const examCoursesOptions = useMemo(() => {
    const examCourseOptions = [
      ...(examCenterCoursesData?.ecGetAllExamCenterCourses.examCenterCourses ?? []),
    ]
      .sort((a, b) => a.examCourse.name.localeCompare(b.examCourse.name))
      .map((course) => ({
        label: course.examCourse.name,
        value: course.examCourse.id,
      }));

    if (examCourseOptions.length === 0) {
      return [];
    }

    return [
      { label: `เลือกทั้งหมด (${examCourseOptions.length})`, value: 'all' },
      ...examCourseOptions,
    ];
  }, [examCenterCoursesData]);

  const setToNull = useCallback(
    (name: Parameters<typeof formContext.setValue>[0]) => {
      formContext.setValue(name, null, { shouldDirty: true });
    },
    [formContext],
  );

  const provincesOptions = useMemo(() => {
    return (
      getAllProvincesResult?.ecGetAllProvinces.provinces
        .map((province) => ({
          label: province.nameTh,
          value: province.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)) ?? []
    );
  }, [getAllProvincesResult]);

  const { data: getLocationsResult, loading: isLoadingLocationOptions } =
    useGetExamCenterLocationsByProvinceIdQuery({
      variables: {
        getExamCenterLocationsByProvinceIdInput: {
          examCenterCode,
          provinceId: location?.province?.value ?? '',
        },
      },
      skip: !isProvinceSelected,
    });

  //REMARK: sort in order by nameTh because the API does not support order by yet
  const locationsOptions = useMemo(() => {
    return (
      getLocationsResult?.ecGetExamCenterLocationsByProvinceId.examCenterLocations
        .map((location) => ({
          label: location.location?.nameTh ?? '',
          value: location.location?.id ?? '',
        }))
        .sort((a, b) => a.label.localeCompare(b.label)) ?? []
    );
  }, [getLocationsResult]);

  const { data: getRoomsResult, loading: isLoadingRoomsOptions } = useSearchExamCenterRoomsQuery({
    variables: {
      searchExamCenterRoomsInput: {
        examCenterCode,
        filter: {
          conditions: [
            {
              field: 'locationId',
              operator: FilterOperator.Equal,
              value: location?.locationName?.value ?? '',
            },
            {
              field: 'status',
              operator: FilterOperator.Equal,
              value: 'ACTIVE',
            },
          ],
        },
        paging: {
          currentPage: 0,
          pageSize: 200,
        },
        order: {
          type: OrderType.Asc,
          field: 'name',
        },
      },
    },
    skip: !location?.locationName?.value,
  });
  const locationRoom = Object.values(location?.room ?? {}).filter((value) => value);

  const isRoomSelected = locationRoom.length > 0;

  const roomsData: RoomData[] = useMemo(() => {
    return (
      getRoomsResult?.ecSearchExamCenterRooms.rooms.map(({ room }) => ({
        id: room.id,
        name: room.name,
        seat: room.seat,
        floor: room.floor,
        roomNumber: room.roomNumber,
        building: room.building.nameTh,
      })) ?? []
    );
  }, [getRoomsResult]);

  const { data: getProctorsResult, loading: isLoadingProctorsOptions } =
    useSearchExamCenterProctorsQuery({
      variables: {
        searchExamCenterProctorsInput: {
          filter: {
            conditions: [
              {
                field: 'examCenterCode',
                operator: FilterOperator.Equal,
                value: examCenterCode,
              },
              {
                field: 'type',
                operator: FilterOperator.Equal,
                value: ExamCenterProctorType.Proctor_1,
              },
            ],
          },
          paging: {
            currentPage: 0,
            pageSize: 200,
          },
          order: {
            type: OrderType.Asc,
            field: 'firstNameTh',
          },
        },
      },
    });
  const proctorsOptions = useMemo(() => {
    return (
      getProctorsResult?.ecSearchExamCenterProctors.examCenterProctors.map((proctor) => ({
        label: [proctor.titleTh, proctor.firstNameTh, proctor.middleNameTh, proctor.lastNameTh]
          .filter(Boolean)
          .join(' '),
        value: proctor.id,
      })) ?? []
    );
  }, [getProctorsResult]);

  const isNameSelected = isSelected(location?.locationName);

  useEffect(() => {
    if (location.room) {
      const selectedRoom = Object.entries(location.room).filter(([, value]) => value);

      const buildingInfo = selectedRoom.map(([id]) => {
        const room = roomsData.find((r) => r.id === id);
        return {
          buildingName: room?.building ?? '',
          roomNumber: room?.roomNumber ?? '',
          floor: room?.floor ?? '',
          roomNameTh: room?.name ?? '',
          seat: room?.seat ?? 0,
        };
      });

      if (buildingInfo.length > 0) {
        setBuilding(buildingInfo);
      }
    }
  }, [locationRoom.length]);

  useEffect(() => {
    if (location.room) {
      const selectedRoom = Object.entries(location.room)
        .filter(([, value]) => value)
        .map(([id]) => id);
      if (selectedRoom.length > 0) {
        const totalSeat = selectedRoom.reduce((acc, curr) => {
          const room = roomsData.find((r) => r.id === curr);
          return acc + (room?.seat ?? 0);
        }, 0);
        formContext.setValue('location.seat', totalSeat);
      } else if (selectedRoom.length === 0 && isNameSelected) {
        formContext.resetField('location.seat');
      }
    }
  }, [locationRoom.length]);

  useEffect(() => {
    if (!examCoursesOptions || examCoursesOptions.length === 0) {
      formContext.setValue('examCourses', []);
      return;
    }

    const allOption = examCoursesOptions.find((course) => course.value === 'all');
    if (!allOption) return;

    formContext.setValue('examCourses', [allOption]);
  }, [examCoursesOptions]);

  const handleSubmitValidation = async (formData: RecurringExamEventCreationFormType) => {
    const isValid = await formContext.trigger(undefined, { shouldFocus: true });

    if (!isValid) {
      return;
    }

    await validatedExamEvents({
      variables: {
        validateExamEventsCreationInput:
          mapRecurringExamEventCreationFormToValidateExamEventsCreationInput(
            formData,
            examCenterCode,
            examCoursesOptions.filter((course) => course.value !== 'all'),
          ),
      },
      onCompleted: (data) => {
        setValidateExamEventsData(
          data.ecValidateExamEventsCreation.validatedExamEvents.map((event) => ({
            ...event,
            // Add a unique requestId for each validated exam event to track row whether it already created or not in the review page for retrying creation of failed exam events
            requestId: randomUUID(),
          })),
        );
        setExamEvent(formData);
        navigate('/:examCenterCode/exam-events/years/:year/months/:month/recurring-create/review', {
          params: {
            examCenterCode,
            year: props.year.param,
            month: props.month.param,
          },
        });
      },
      onError: (error) => {
        const errorCode = getExamEventCreationErrorCode(error);
        let errorMessage = 'เกิดข้อผิดพลาดในการตรวจสอบรอบสอบ';

        if (errorCode && EXAM_EVENT_CREATION_ERROR_MESSAGE_MAP[errorCode]) {
          errorMessage = EXAM_EVENT_CREATION_ERROR_MESSAGE_MAP[errorCode];
        }

        showSnackbar({
          message: errorMessage,
          variant: 'error',
        });
      },
    });
  };

  const handleCancel = () => {
    navigate('/:examCenterCode/exam-events/years/:year/months/:month', {
      replace: true,
      params: {
        examCenterCode,
        year: yearParam,
        month: monthParam,
      },
    });
  };

  useEffect(() => {
    const { unsubscribe } = formContext.watch((value, { name }) => {
      if (name === 'location.locationName') {
        setToNull('location.room');
        setToNull('location.seat');
      }

      if (name === 'location.province') {
        setToNull('location.locationName');
        setToNull('location.room');
        setToNull('location.seat');
      }
      if (name === 'location.room') {
        setToNull('location.seat');
      }
      if (name === 'eventEndDate.type') {
        if (value?.eventEndDate?.type !== EventEndType.BEFORE_EXAM_DATE) {
          setToNull('eventEndDate.date');
        }
      }
    });

    return () => unsubscribe();
  }, [formContext, setToNull, examEvent]);

  const selectedMonth = new Date(year, month);
  const minimumExamDate = dayjs(selectedMonth).startOf('month').toDate();
  const maximumExamDate = dayjs(selectedMonth).endOf('month').toDate();

  if (isLoading || isLoadingProctorsOptions || isLoadingProvincesOptions) {
    return <Loading sx={{ height: '100dvh' }} />;
  }

  return (
    <RecurringExamEventCreationForm
      form={formContext}
      isCreating={isValidatingExamEvents}
      onSubmit={handleSubmitValidation}
      onCancel={handleCancel}
      slotProps={{
        examDate: {
          isExamDateSelected: true,
        },
        proctor: {
          mainProctorOptions: proctorsOptions,
          isLoading: isLoadingProctorsOptions,
        },
        examCourses: {
          examCoursesOptions,
          isExamCourseSelected: (formContext.watch('examCourses') ?? []).length > 0,
          examCourseSelected: formContext.watch('examCourses') ?? [],
          setSelected: (courses) => formContext.setValue('examCourses', courses),
          isLoading,
          multiple: true,
        },
        province: {
          isProvinceSelected,
          isProvincesOptionsLoading: isLoadingProvincesOptions,
          provincesOptions,
          isLocationOptionsLoading: isLoadingLocationOptions,
          locationsOptions,
          isGetAvailableRoomsLoading: isLoadingRoomsOptions,
        },
        room: {
          roomData: roomsData,
          isRoomSelected,
        },
        name: {
          isNameSelected: !!location?.locationName?.value,
        },
        eventEndDate: {
          type: formContext.watch('eventEndDate.type') ?? undefined,
          errorMessage: formContext.formState.errors.eventEndDate?.root?.message,
          examRecurringStartDate,
        },
        examDateGroup: {
          examRecurringEndDate,
          minDate: minimumExamDate,
          maxDate: maximumExamDate,
        },
      }}
    />
  );
}
